import React, {useEffect, useRef} from 'react';
import ReactPaginate from 'react-paginate';

const NAV_LABEL = {
    top: 'Products pagination above the list',
    bottom: 'Products pagination below the list',
} as const;

type StoreProductPaginationProps = {
    placement: keyof typeof NAV_LABEL;
    pageNumber: number;
    pageCount: number;
    loading: boolean;
    hrefBuilder: (pageNumber: number) => string;
    onPageChange: (selectedIndex: number) => void;
};

/** ReactPaginate prevents default before its onClick callback, so native link gestures must bypass it in capture. */
export function preserveNativePaginationNavigation(event: React.MouseEvent<HTMLElement>) {
    if ((event.button !== 0 || event.ctrlKey || event.metaKey || event.shiftKey || event.altKey) && (event.target as Element).closest('a[href]')) {
        event.stopPropagation();
    }
}

function stripNestedPaginationLabel(nav: HTMLElement | null) {
    const paginationList = nav?.querySelector('ul.pagination');

    if (!paginationList) {
        return;
    }

    paginationList.removeAttribute('role');
    paginationList.removeAttribute('aria-label');
}

export const StoreProductPagination: React.FC<StoreProductPaginationProps> = ({placement, pageNumber, pageCount, loading, hrefBuilder, onPageChange}) => {
    const navRef = useRef<HTMLElement>(null);

    useEffect(() => {
        stripNestedPaginationLabel(navRef.current);
    });

    return (
        <nav
            ref={navRef}
            aria-label={NAV_LABEL[placement]}
            onClickCapture={preserveNativePaginationNavigation}
        >
            <ReactPaginate
                previousLabel="Prev"
                nextLabel="Next"
                breakLabel="..."
                containerClassName={`pagination${loading ? ' loading' : ''}`}
                activeClassName="active"
                marginPagesDisplayed={2}
                pageRangeDisplayed={3}
                forcePage={pageNumber - 1}
                pageCount={pageCount}
                hrefBuilder={hrefBuilder}
                selectedPageRel={null}
                onClick={({nextSelectedPage}) => {
                    // ReactPaginate moves its own selection on click and re-syncs to forcePage only when the prop changes.
                    // Report the click and keep its selection on the accepted page, so a failed request leaves no stale pager.
                    if (nextSelectedPage !== undefined) {
                        onPageChange(nextSelectedPage);
                    }

                    return false;
                }}
            />
        </nav>
    );
};
